import React, { useEffect, useState } from "react";
import { Link2, Copy, MessageCircle, Mail, Smartphone, Loader2, Search, X, Check } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const DRIVER_KINDS = [
  ["id_recto", "Pièce d'identité — recto"],
  ["id_verso", "Pièce d'identité — verso"],
  ["driving_license_recto", "Permis — recto"],
  ["driving_license_verso", "Permis — verso"],
  ["insurance", "Attestation d'assurance"],
  ["carte_grise", "Carte grise"],
];

// Admin : crée un lien (30 j, sans connexion) pour qu'un covoitureur envoie ses documents.
export default function IdentityLinkDialog({ onClose }) {
  const [q, setQ] = useState("");
  const [drivers, setDrivers] = useState([]);
  const [picked, setPicked] = useState(null);
  const [kinds, setKinds] = useState(DRIVER_KINDS.map(([k]) => k));
  const [busy, setBusy] = useState(false);
  const [link, setLink] = useState(null);

  useEffect(() => {
    const t = setTimeout(async () => {
      try {
        const r = await api.get("/admin/price/drivers/slim", { params: { q: q.trim() || undefined, limit: 20 } });
        setDrivers(r.data?.items || []);
      } catch (e) { toast.error(formatApiError(e)); }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const create = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/price/identity/links", { user_id: picked.user_id, kinds });
      setLink(r.data);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const toggle = (k) => setKinds((ks) => (ks.includes(k) ? ks.filter((x) => x !== k) : [...ks, k]));
  const name = picked ? (picked.full_name || `${picked.first_name || ""} ${picked.last_name || ""}`.trim() || picked.email) : "";

  return (
    <div className="fixed inset-0 z-[90] grid place-items-center bg-black/50 p-3" onClick={onClose} data-testid="identity-link-dialog">
      <div className="w-full max-w-md max-h-[90dvh] overflow-y-auto rounded-3xl bg-[#FFF8F1] p-5 street-border street-shadow" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 font-[Outfit] text-[17px] font-black"><Link2 className="h-5 w-5 text-primary" /> Lien documents</div>
          <button type="button" onClick={onClose} data-testid="identity-link-close" className="grid h-9 w-9 place-items-center rounded-xl bg-white street-border-thin" aria-label="Fermer"><X className="h-4 w-4" /></button>
        </div>
        {link ? (
          <LinkResult link={link} name={name} onReset={() => { setLink(null); setPicked(null); setKinds(DRIVER_KINDS.map(([k]) => k)); }} />
        ) : (
          <>
            {!picked ? (
              <>
                <label className="mt-4 flex items-center gap-2 rounded-xl bg-white px-3 street-border-thin">
                  <Search className="h-4 w-4 text-foreground/50" />
                  <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nom, e-mail ou téléphone du conducteur" data-testid="identity-link-search" className="h-11 flex-1 bg-transparent text-[14px] font-semibold focus:outline-none" />
                </label>
                <div className="mt-2 max-h-64 space-y-1 overflow-y-auto">
                  {drivers.map((d) => (
                    <button key={d.user_id} type="button" onClick={() => setPicked(d)} data-testid={`identity-link-driver-${d.user_id}`} className="w-full rounded-xl bg-white px-3 py-2 text-left street-border-thin hover:bg-secondary/40">
                      <div className="text-[13px] font-black">{d.full_name || `${d.first_name || ""} ${d.last_name || ""}`.trim() || "—"}</div>
                      <div className="text-[11px] text-foreground/60">{d.email}{d.phone ? ` · ${d.phone}` : ""}</div>
                    </button>
                  ))}
                  {drivers.length === 0 && <div className="py-4 text-center text-[12px] text-foreground/50">Aucun conducteur trouvé</div>}
                </div>
              </>
            ) : (
              <>
                <div className="mt-4 flex items-center justify-between rounded-xl bg-white px-3 py-2 street-border-thin" data-testid="identity-link-picked">
                  <div><div className="text-[13px] font-black">{name}</div><div className="text-[11px] text-foreground/60">{picked.email}</div></div>
                  <button type="button" onClick={() => setPicked(null)} data-testid="identity-link-change" className="text-[11px] font-black uppercase text-primary">Changer</button>
                </div>
                <div className="mt-3 text-[11px] font-black uppercase tracking-widest text-foreground/50">Documents demandés</div>
                <div className="mt-1.5 grid grid-cols-1 gap-1.5">
                  {DRIVER_KINDS.map(([k, l]) => (
                    <button key={k} type="button" onClick={() => toggle(k)} data-testid={`identity-link-kind-${k}`} className={`flex items-center gap-2 rounded-xl px-3 py-2 text-left text-[13px] font-bold street-border-thin ${kinds.includes(k) ? "bg-[#D6F5D6]" : "bg-white"}`}>
                      <span className={`grid h-5 w-5 place-items-center rounded-md border-2 border-black ${kinds.includes(k) ? "bg-primary text-white" : "bg-white"}`}>{kinds.includes(k) && <Check className="h-3 w-3" strokeWidth={4} />}</span>{l}
                    </button>
                  ))}
                </div>
                <button type="button" disabled={busy || kinds.length === 0} onClick={create} data-testid="identity-link-create" className="street-press mt-4 flex w-full items-center justify-center gap-2 rounded-2xl bg-primary py-3.5 font-[Outfit] text-[15px] font-black text-primary-foreground street-border street-shadow disabled:opacity-50">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />} Créer le lien
                </button>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function LinkResult({ link, name, onReset }) {
  const msg = `Salut ${link.first_name || ""} ! Pour finaliser ton compte conducteur Price, envoie tes documents ici (lien sécurisé, valable 30 jours) : ${link.url}`;
  const copy = async () => {
    try { await navigator.clipboard.writeText(link.url); toast.success("Lien copié"); } catch (_e) { window.prompt("Copie le lien :", link.url); }
  };
  const phone = (link.phone || "").replace(/[^\d+]/g, "");
  const wa = `https://wa.me/${phone.replace(/^\+/, "").replace(/^0/, "33")}?text=${encodeURIComponent(msg)}`;
  return (
    <div className="mt-4" data-testid="identity-link-result">
      <div className="text-[13px] font-bold">Lien prêt pour <b>{name}</b> ✅</div>
      <div className="mt-2 break-all rounded-xl bg-white p-3 text-[12px] font-mono street-border-thin" data-testid="identity-link-url">{link.url}</div>
      <div className="mt-3 grid grid-cols-2 gap-2">
        <button type="button" onClick={copy} data-testid="identity-link-copy" className="street-press flex items-center justify-center gap-1.5 rounded-xl bg-primary py-2.5 text-[13px] font-black text-primary-foreground street-border-thin"><Copy className="h-4 w-4" /> Copier</button>
        <a href={wa} target="_blank" rel="noreferrer" data-testid="identity-link-whatsapp" className="street-press flex items-center justify-center gap-1.5 rounded-xl bg-[#25D366] py-2.5 text-[13px] font-black text-white street-border-thin"><MessageCircle className="h-4 w-4" /> WhatsApp</a>
        <a href={`sms:${phone}?&body=${encodeURIComponent(msg)}`} data-testid="identity-link-sms" className="street-press flex items-center justify-center gap-1.5 rounded-xl bg-white py-2.5 text-[13px] font-black street-border-thin"><Smartphone className="h-4 w-4" /> SMS</a>
        <a href={`mailto:${link.email || ""}?subject=${encodeURIComponent("Price — tes documents conducteur")}&body=${encodeURIComponent(msg)}`} data-testid="identity-link-email" className="street-press flex items-center justify-center gap-1.5 rounded-xl bg-white py-2.5 text-[13px] font-black street-border-thin"><Mail className="h-4 w-4" /> E-mail</a>
      </div>
      {!phone && <p className="mt-2 text-[11px] font-bold text-amber-700" data-testid="identity-link-no-phone">Pas de numéro enregistré : WhatsApp/SMS ouvriront le choix du contact.</p>}
      <p className="mt-3 text-[11px] text-foreground/60">Tu recevras une notification dès qu&apos;il aura envoyé ses documents.</p>
      <button type="button" onClick={onReset} data-testid="identity-link-new" className="mt-3 w-full rounded-xl bg-white py-2.5 text-[12px] font-black uppercase tracking-widest street-border-thin">Créer un autre lien</button>
    </div>
  );
}
